import { useAppSelector } from '@/app/hooks';
import { selectUser } from '@/entities/user';
import type { Wallet } from '@/types/api';
import { formatMoney } from '@/shared/lib/numberFormat';
import { pickTodayIncomeTotal } from '../lib/todayIncome';
import { ProfitInfoHeader } from './ProfitInfoHeader';
import { ProfitInfoMenu } from './ProfitInfoMenu';
import { ProfitInfoPill } from './ProfitInfoPill';

// Адаптация под наш API. У фронтендера были два endpoint'а (/user/balance/
// + /user/profit-for-period/) с shape {by_currencies, total} → {main, hold}.
// У нас балансы и заработок лежат в /me:
//   - me.today_incomes — массив по валютам; берём рублёвую строку (pickTodayIncomeTotal)
//   - me.wallets[] — массив { currency_id, balance: string, currency? }
//
// Главное упрощение: у нас одно число балансa per wallet (без разделения на
// main/hold). Берём первый wallet (если RUB не выделен — он первым обычно),
// либо суммируем все балансы (TBD: уточнить с бизнесом, как считать
// "Кошельки" если их несколько валют).

type ProfitInfoProps = {
  // 'pill' — строка аккаунта над контентом (layout.header = 'in-content');
  // 'amount' — только сумма основного кошелька, оформление целиком от className
  // (верхняя панель и карточка профиля у compactNav = 'rail-topbar').
  variant: 'header' | 'menu' | 'pill' | 'amount';
  // Закрыть мобильную шторку при переходе по кошельку (см. ProfitInfoMenu).
  onNavigate?: () => void;
  // Только для 'pill': тёмная маленькая пилюля с одной суммой (верхняя панель
  // телефона, см. ProfitInfoPill).
  compact?: boolean;
  className?: string;
};

// Символ валюты кошелька. Фолбэк на `#id` — как в Header: у кошелька в
// экзотической валюте currency может не прийти, но показать его надо.
const walletIco = (wallet: Wallet): string => wallet.currency?.ico ?? `#${wallet.currency_id}`;

const pickPrimaryWallet = (wallets: Wallet[] | undefined): Wallet | undefined => {
  if (!wallets?.length) return undefined;
  // Пытаемся найти рублевый кошелёк — currency.ico часто содержит '₽' либо
  // currency.name === 'RUB' / 'руб'. Если не нашли — возвращаем первый.
  const rubWallet = wallets.find((w) => {
    const ico = w.currency?.ico ?? '';
    const name = w.currency?.name ?? '';
    return ico.includes('₽') || /руб|RUB/i.test(name);
  });
  return rubWallet ?? wallets[0];
};

export const ProfitInfo = ({ variant, onNavigate, compact, className }: ProfitInfoProps) => {
  const user = useAppSelector(selectUser);

  const wallets = user?.wallets ?? [];
  const primaryWallet = pickPrimaryWallet(wallets);

  // «За сегодня» — рублёвая строка today_incomes (см. pickTodayIncomeTotal),
  // поэтому символ рубля здесь захардкожен; у баланса символ берётся из его
  // собственной валюты.
  const periodProfitAmount = `${formatMoney(pickTodayIncomeTotal(user?.today_incomes))} ₽`;
  const userBalanceAmount = primaryWallet
    ? `${formatMoney(primaryWallet.balance)} ${walletIco(primaryWallet)}`
    : `${formatMoney(0)} ₽`;

  const walletOptions = wallets.map((w) => ({ id: w.id, label: `${formatMoney(w.balance)} ${walletIco(w)}` }));

  if (variant === 'amount') {
    return <span className={className}>{userBalanceAmount}</span>;
  }

  if (variant === 'pill') {
    return (
      <ProfitInfoPill
        className={className}
        compact={compact}
        userBalanceAmount={userBalanceAmount}
        wallets={walletOptions}
      />
    );
  }

  if (variant === 'menu') {
    return (
      <ProfitInfoMenu
        className={className}
        onNavigate={onNavigate}
        periodProfitAmount={periodProfitAmount}
        userBalanceAmount={userBalanceAmount}
        wallets={walletOptions}
      />
    );
  }

  return (
    <ProfitInfoHeader
      periodProfitAmount={periodProfitAmount}
      userBalanceAmount={userBalanceAmount}
    />
  );
};
